import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';

import { Button } from '@/components/Button';
import { TextField } from '@/components/TextField';
import { sendPasswordReset, AuthActionError } from '@/lib/auth-actions';
import { resetRequestSchema, type ResetRequestValues } from '@/lib/auth-schemas';
import { useRecaptchaToken } from '@/lib/useRecaptchaToken';
import { verifyRecaptcha } from '@/lib/recaptcha';
import { env } from '@/lib/env';

export function ResetPasswordPage() {
  const { t } = useTranslation(['auth']);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const getRecaptchaToken = useRecaptchaToken();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetRequestValues>({
    resolver: zodResolver(resetRequestSchema(t)),
    defaultValues: { email: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      // reCAPTCHA v3 (Spec V1.1 §M5 L842, §M13.6). action='reset_request'
      // — server-side rate-limit 3/24h/email (🔹 Déduit aligned with
      // contact, Matrice §3.8). reCAPTCHA error codes ARE surfaced as
      // toasts: they don't leak account existence (the bot signal is
      // independent of whether the email is registered).
      const token = await getRecaptchaToken('reset_request');
      if (!token && env.recaptchaSiteKey) throw new AuthActionError('recaptcha_failed');
      if (token) {
        const verdict = await verifyRecaptcha({
          token,
          action: 'reset_request',
          email: values.email,
        });
        if (!verdict.success) {
          throw new AuthActionError(
            verdict.code === 'rate_limited' ? 'recaptcha_rate_limited' : 'recaptcha_failed',
          );
        }
      }
      await sendPasswordReset(values.email);
    } catch (error) {
      if (error instanceof AuthActionError) {
        if (error.code === 'recaptcha_failed') {
          toast.error(t('auth:errors.recaptchaFailed'));
          setSubmitting(false);
          return;
        }
        if (error.code === 'recaptcha_rate_limited') {
          toast.error(t('auth:errors.recaptchaRateLimited'));
          setSubmitting(false);
          return;
        }
        // Spec V1.1 — privacy: never leak whether the account exists.
        // Supabase's own rate_limited is the only generic-error path we
        // surface; everything else falls through to the neutral banner.
        if (error.code === 'rate_limited') {
          toast.error(t('auth:errors.rateLimited'));
          setSubmitting(false);
          return;
        }
      }
    }
    setSent(true);
    setSubmitting(false);
  });

  return (
    <section className="m5-shell">
      <h1 className="m5-shell__title">{t('auth:reset.requestTitle')}</h1>
      <p className="m5-shell__lead">{t('auth:reset.requestLead')}</p>

      <div className="m5-card">
        {sent ? (
          <p className="m5-banner m5-banner--success" role="status">
            {t('auth:reset.requestSent')}
          </p>
        ) : (
          <form className="m5-form" onSubmit={onSubmit} noValidate>
            <TextField
              label={t('auth:fields.email')}
              type="email"
              autoComplete="email"
              inputMode="email"
              required
              error={errors.email?.message}
              {...register('email')}
            />
            <Button
              type="submit"
              variant="primary"
              loading={submitting}
              loadingLabel={t('auth:common.loading')}
            >
              {t('auth:reset.requestSubmit')}
            </Button>
          </form>
        )}

        <p className="m5-footnote">
          <Link to="/signin" className="m5-link">
            {t('auth:reset.backToSignIn')}
          </Link>
        </p>

        <p className="m5-recaptcha-notice">
          <Trans
            i18nKey="auth:common.recaptchaNotice"
            components={{
              privacy: (
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="m5-link"
                />
              ),
              terms: (
                <a
                  href="https://policies.google.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="m5-link"
                />
              ),
            }}
          />
        </p>
      </div>
    </section>
  );
}
